import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Form,
  Row,
  Col,
  Table,
  Spinner,
  Badge,
  InputGroup,
} from "react-bootstrap";
import { FaFilePdf, FaSync, FaListUl, FaTable, FaSearch } from "react-icons/fa";
import { useConfig } from "../../utensils/ConfigContext";
import { getAllReservations } from "../../market/warehouse-baserow/useAdminReservation";
import "./SnackReportReservations.css";

// Meses en español
const MONTHS = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const niceDateTime = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("es-CO", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

// Normaliza texto: minúsculas y sin acentos
const norm = (s) =>
  (s ?? "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

const SnackReportReservations = () => {
  const { apiToken } = useConfig();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);

  // Filtros UI
  const [filterYear, setFilterYear] = useState("todos");
  const [filterMonth, setFilterMonth] = useState("todos");
  const [filterQuery, setFilterQuery] = useState(""); // 🔎 término de búsqueda
  const [viewMode, setViewMode] = useState("consolidado"); // "consolidado" | "detalles"

  const printRef = useRef(null);

  const fetchAll = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getAllReservations({ apiToken });
      setRows(data || []);
    } catch (e) {
      setError(e.message || "Error cargando reservas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (apiToken) fetchAll();
  }, [apiToken]);

  // Años disponibles en los datos
  const availableYears = useMemo(() => {
    const ys = new Set();
    rows.forEach((r) => {
      const d = new Date(r["Creado en"]);
      if (!isNaN(d)) ys.add(d.getFullYear());
    });
    return Array.from(ys).sort((a, b) => b - a); // descendente
  }, [rows]);

  // Filtrado por año/mes + búsqueda
  const filteredRows = useMemo(() => {
    const tokens = norm(filterQuery).split(/\s+/).filter(Boolean); // múltiples palabras

    return rows.filter((r) => {
      // 1) Filtro por año/mes
      const d = new Date(r["Creado en"]);
      if (isNaN(d)) return false;
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      const passYear = filterYear === "todos" ? true : y === Number(filterYear);
      const passMonth =
        filterMonth === "todos" ? true : m === Number(filterMonth);
      if (!(passYear && passMonth)) return false;

      // 2) Filtro por término de búsqueda
      if (tokens.length === 0) return true;

      const haystack = norm(
        [
          r?.Nombre,
          r?.Email,
          r?.Clase,
          r?.Reserva,
          r?.id,
          niceDateTime(r?.["Creado en"]),
        ]
          .filter((x) => x !== undefined && x !== null)
          .join(" ")
      );

      // Deben coincidir TODOS los tokens
      return tokens.every((t) => haystack.includes(t));
    });
  }, [rows, filterYear, filterMonth, filterQuery]);

  // KPIs (sobre los datos filtrados)
  const kpis = useMemo(() => {
    const total = filteredRows.length;
    const activas = filteredRows.reduce(
      (acc, r) => acc + (r.Estado ? 1 : 0),
      0
    );
    const canceladas = total - activas;
    const usuarios = new Set(filteredRows.map((r) => r.Email).filter(Boolean));
    const clases = new Set(filteredRows.map((r) => r.Clase).filter(Boolean));
    return {
      total,
      activas,
      canceladas,
      usuarios: usuarios.size,
      clases: clases.size,
    };
  }, [filteredRows]);

  // Agrupado mensual (para consolidado)
  const monthly = useMemo(() => {
    const map = new Map(); // key: "YYYY-MM"
    filteredRows.forEach((r) => {
      const d = new Date(r["Creado en"]);
      if (isNaN(d)) return;
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      const key = `${y}-${String(m).padStart(2, "0")}`;
      if (!map.has(key)) {
        map.set(key, {
          year: y,
          month: m,
          total: 0,
          activas: 0,
          canceladas: 0,
          usuarios: new Set(),
          clases: new Set(),
        });
      }
      const g = map.get(key);
      g.total += 1;
      if (r.Estado) g.activas += 1;
      else g.canceladas += 1;
      if (r.Email) g.usuarios.add(r.Email);
      if (r.Clase) g.clases.add(r.Clase);
    });

    const arr = Array.from(map.values()).sort(
      (a, b) => b.year - a.year || b.month - a.month
    );
    return arr.map((g) => ({
      ...g,
      usuarios: g.usuarios.size,
      clases: g.clases.size,
    }));
  }, [filteredRows]);

  const handleGeneratePDF = () => {
    // Imprime el área del reporte (usa estilos @media print del CSS)
    window.print();
  };

  return (
    <div className="training-plan-container">
      <div className="d-flex justify-content-between align-items-center p-3 bg-white rounded shadow-sm mb-4 border">
        <h2 className="m-0">Reporte de reservas</h2>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={fetchAll}>
            <FaSync className="me-1" /> Refrescar
          </Button>
          <Button variant="danger" onClick={handleGeneratePDF}>
            <FaFilePdf className="me-1" /> Generar PDF
          </Button>
        </div>
      </div>

      {/* Toolbar filtros */}
      <div className="report-toolbar mb-3 no-print">
        <Row className="g-2 align-items-end">
          <Col xs={12} md={3}>
            <Form.Label>Año</Form.Label>
            <Form.Select
              value={filterYear}
              onChange={(e) => {
                setFilterYear(e.target.value);
                // si cambia año, mantener mes o resetear a "todos" (comportamiento actual: mantener)
              }}
            >
              <option value="todos">Todos</option>
              {availableYears.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={12} md={3}>
            <Form.Label>Mes</Form.Label>
            <Form.Select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
            >
              <option value="todos">Todos</option>
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>
                  {m}
                </option>
              ))}
            </Form.Select>
          </Col>

          {/* 🔎 Búsqueda */}
          <Col xs={12} md={3}>
            <Form.Label>Búsqueda</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Nombre, email, clase, reserva, ID…"
                value={filterQuery}
                onChange={(e) => setFilterQuery(e.target.value)}
                autoComplete="off"
              />
            </InputGroup>
          </Col>

          <Col xs={12} md={3} className="d-flex gap-2">
            <div className="view-switch w-100 d-flex">
              <Button
                className="flex-fill"
                variant={viewMode === "consolidado" ? "dark" : "outline-dark"}
                onClick={() => setViewMode("consolidado")}
              >
                <FaTable className="me-1" />
                Consolidado
              </Button>
              <Button
                className="flex-fill"
                variant={viewMode === "detalles" ? "dark" : "outline-dark"}
                onClick={() => setViewMode("detalles")}
              >
                <FaListUl className="me-1" />
                Detalles
              </Button>
            </div>
          </Col>
        </Row>
      </div>

      {/* KPIs */}
      <div className="kpi-grid no-print">
        <div className="kpi-card">
          <div className="kpi-label">Total reservas</div>
          <div className="kpi-value">{kpis.total}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Activas</div>
          <div className="kpi-value text-success">{kpis.activas}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Canceladas</div>
          <div className="kpi-value text-danger">{kpis.canceladas}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Usuarios únicos</div>
          <div className="kpi-value">{kpis.usuarios}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Clases distintas</div>
          <div className="kpi-value">{kpis.clases}</div>
        </div>
      </div>

      {/* Contenido imprimible */}
      <div
        ref={printRef}
        id="reportPrintable"
        className="report-surface card shadow-sm p-3"
      >
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : viewMode === "consolidado" ? (
          <>
            <h5 className="mb-3">Consolidado mensual</h5>
            <div className="table-responsive table-sticky">
              <Table bordered hover className="align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 120 }}>Año</th>
                    <th style={{ minWidth: 160 }}>Mes</th>
                    <th>Reservas</th>
                    <th>Activas</th>
                    <th>Canceladas</th>
                    <th>Usuarios únicos</th>
                    <th>Clases</th>
                  </tr>
                </thead>
                <tbody>
                  {monthly.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-muted">
                        Sin datos para los filtros seleccionados
                      </td>
                    </tr>
                  ) : (
                    monthly.map((m) => (
                      <tr key={`${m.year}-${m.month}`}>
                        <td>{m.year}</td>
                        <td>{MONTHS[m.month - 1]}</td>
                        <td>
                          <strong>{m.total}</strong>
                        </td>
                        <td className="text-success">{m.activas}</td>
                        <td className="text-danger">{m.canceladas}</td>
                        <td>{m.usuarios}</td>
                        <td>{m.clases}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </>
        ) : (
          <>
            <h5 className="mb-3">Detalles de reservas</h5>
            <div className="table-responsive table-sticky">
              <Table bordered hover className="align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 80 }}>ID</th>
                    <th style={{ minWidth: 200 }}>Creado en</th>
                    <th style={{ minWidth: 220 }}>Reserva</th>
                    <th style={{ minWidth: 180 }}>Nombre</th>
                    <th style={{ minWidth: 220 }}>Email</th>
                    <th style={{ width: 100 }}>Clase</th>
                    <th style={{ width: 110 }}>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-muted">
                        Sin datos para los filtros seleccionados
                      </td>
                    </tr>
                  ) : (
                    filteredRows
                      .sort(
                        (a, b) =>
                          new Date(b["Creado en"]) - new Date(a["Creado en"])
                      )
                      .map((r) => (
                        <tr key={r.id}>
                          <td>{r.id}</td>
                          <td>{niceDateTime(r["Creado en"])}</td>
                          <td className="font-monospace">{r.Reserva || "-"}</td>
                          <td>{r.Nombre || "-"}</td>
                          <td>{r.Email || "-"}</td>
                          <td>{r.Clase ?? "-"}</td>
                          <td>
                            {r.Estado ? (
                              <Badge bg="success">Activa</Badge>
                            ) : (
                              <Badge bg="secondary">Cancelada</Badge>
                            )}
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SnackReportReservations;
